/**
 * SitemapCanvas — xyflow canvas for the website editor (T3.8, T3.9).
 *
 * Shows the site's pages as nodes (each node = one PageNode with an
 * embedded Grid). Navigation links between pages are edges.
 *
 * Web target uses @xyflow/react. The native Expo build will swap to
 * @fredericrous/xyflow-react-native at T3.8-native time; the logic is
 * identical between the two.
 *
 * Provider hierarchy (inside-out):
 *   <EditorProvider>        ← shares Y.Doc with PageNode via context
 *     <ReactFlowProvider>   ← xyflow store
 *       <InnerCanvas />     ← accesses both via hooks
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  MiniMap,
  Panel,
  addEdge,
  useNodesState,
  useEdgesState,
  useStoreApi,
  useReactFlow,
  type Node,
  type Edge,
  type Connection,
  type OnConnect,
  type OnNodeDrag,
  type NodeTypes,
} from "./xyflow.js";
import "./editorStyles.js";
import {
  bindYjsToFlow,
  LOCAL_ORIGIN as FLOW_LOCAL_ORIGIN,
  type FlowStore,
} from "@website-builder/y-xyflow";
import { EditorProvider } from "./EditorContext.js";
import { ReadOnlyProvider } from "./ReadOnlyContext.js";
import { useAddDrag } from "./AddDragContext.js";
import { useGridStore } from "@fredericrous/grid";
import {
  useWebCrossGridDrag,
  type UseWebCrossGridDragReturn,
} from "@fredericrous/grid/headless";
import { crossGridAccepts, itemForKind } from "./crossGridAccepts.js";
import { useCrossTabDrop } from "./useCrossTabDrop.js";
import { useCrossWindowDrag } from "./crossWindowDrag.js";
import { PageNode, type PageNodeData } from "./PageNode.js";
import { PAGE_WIDTH, PAGE_HEIGHT, useAuthoringWidth } from "./usePageNode.js";
import { readInitialSitemap } from "./initialSitemap.js";
import { PAGE_TEMPLATES, createPageNodeFromTemplate } from "./pageTemplates.js";
import {
  reparent,
  detach,
  parentOf,
  candidateParents,
  pickDropParent,
  type DropCandidate,
} from "./pageHierarchy.js";
import { layoutSitemap, layoutInputKey } from "./sitemapLayout.js";
import { SitemapInteractionProvider } from "./SitemapInteractionContext.js";
import { EditorMenu, EditorMenuItem } from "./ui/EditorMenu.js";
import { EditorButton } from "./ui/EditorButton.js";
import { CanvasToggle } from "./ui/CanvasToggle.js";
import { FlowCanvasFrame } from "./ui/FlowCanvasFrame.js";
import { CanvasToolsPanel } from "./CanvasToolsPanel.js";
import { CanvasTrashZone, TRASH_ZONE_ID } from "./CanvasTrashZone.js";
import { publishCanvasZoom } from "./canvasZoom.js";
import {
  navParentOf,
  navChildrenOf,
  type NavEdge,
} from "@website-builder/schemas";
import { EDIT_MIN_ZOOM } from "./canvasZoom.js";
import { useSharedRegions } from "./sharedRegions.js";
import { ChromeBox } from "./ui/ChromeBox.js";
import { BreakpointSwitcher } from "./BreakpointSwitcher.js";
import { BreakpointHint } from "./BreakpointHint.js";
import { SlugEditor } from "./SlugEditor.js";
import type { Breakpoint } from "./breakpoints.js";
import { setActiveBreakpoint } from "@website-builder/y-grid";
import { purgePageGrids } from "./purgePageGrids.js";
import type { EditorDoc } from "./useEditorDoc.js";
import { html, css } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { microSpacing } from "@duro-app/tokens/tokens/spacing.css";

/** ms the dragged node's center must dwell over a target before it commits as
 *  the drop-parent — makes the gesture deliberate, not a mere passing overlap. */
const DROP_DWELL_MS = 350;

function nodeBox(n: Node): DropCandidate {
  return {
    id: n.id,
    x: n.position.x,
    y: n.position.y,
    w: n.measured?.width ?? n.width ?? 320,
    h: n.measured?.height ?? n.height ?? 480,
  };
}

interface NodeMenuState {
  id: string;
  x: number;
  y: number;
}

// ── Custom node types ─────────────────────────────────────────────

// Defined outside the render function so xyflow's type registry is
// stable across re-renders (avoids re-mounting all nodes).
const NODE_TYPES: NodeTypes = {
  page: PageNode as never,
};

/** Gap (px) left between a page and the one above it when auto-placing. */
const NEW_PAGE_GAP = 48;

const canvasChrome = css.create({
  // Toolbar + the contextual override strip stacked beneath it.
  toolbarStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: microSpacing.px6,
  },
  // ChromeBox's toast is the contrast surface; its text takes the matching ink.
  toastText: { color: colors.onContrastSurface },
});

/**
 * Place a NEW page directly below `target`, clear of every other node it would
 * overlap — "auto-arrange just this one page" (it never moves existing nodes,
 * unlike the whole-graph Tidy layout). Every rect uses the node's MEASURED size,
 * i.e. the WHOLE node (titlebar + header band + body + footer band), so the new
 * page lands under the parent's real bottom instead of a guessed offset — and a
 * new page is assumed to be about as tall as its parent for the overlap check.
 */
function positionBelow(
  target: Node<PageNodeData> | undefined,
  nodes: ReadonlyArray<Node<PageNodeData>>,
): { x: number; y: number } {
  const px = target?.position.x ?? 80;
  const py = target?.position.y ?? 80;
  const pw = target?.measured?.width ?? target?.width ?? PAGE_WIDTH;
  const ph = target?.measured?.height ?? target?.height ?? PAGE_HEIGHT;
  const newH = ph; // a fresh page ≈ its parent's full-node height
  let y = py + ph + NEW_PAGE_GAP;
  for (let guard = 0, changed = true; changed && guard < 200; guard++) {
    changed = false;
    for (const n of nodes) {
      if (n.id === target?.id) continue;
      const nx = n.position.x;
      const nw = n.measured?.width ?? n.width ?? PAGE_WIDTH;
      const ny = n.position.y;
      const nh = n.measured?.height ?? n.height ?? PAGE_HEIGHT;
      const horizontallyOverlaps = px < nx + nw && px + pw > nx;
      if (horizontallyOverlaps && y < ny + nh + NEW_PAGE_GAP && y + newH > ny) {
        y = ny + nh + NEW_PAGE_GAP;
        changed = true;
      }
    }
  }
  return { x: px, y };
}

// ── Inner canvas ─────────────────────────────────────────────────

const NOOP_STARTER = () => {};

/** Mirror the live canvas zoom into the shared `canvasZoom` signal so chrome
 *  OUTSIDE the ReactFlowProvider (the Add palette) can react to it. A leaf
 *  component subscribed to ONLY the zoom scalar, so it re-renders on zoom but
 *  not on pan, and never re-renders its siblings. */
function CanvasZoomPublisher() {
  const store = useStoreApi();
  const zoom = useSyncExternalStore(
    store.subscribe,
    () => store.getState().transform[2],
  );
  useEffect(() => {
    publishCanvasZoom(zoom);
  }, [zoom]);
  return null;
}

/** Mount the grid's web cross-grid adapter once and RETURN its starter (shared
 *  via SitemapInteractionContext, which already crosses xyflow's node-render
 *  boundary down to each item) plus the 0.9.4 session controls the
 *  cross-window carry needs: `cancel()` (end the live pointer session — no
 *  more synthetic-Escape dispatch) and `startCrossGridDragAt` (restart the
 *  move drag from a point on ⌥ release). Inert in read-only preview. The store
 *  commits the transfer atomically on drop, so onDrop has nothing to do. */
function useCrossGridDrag(
  readOnly: boolean,
  trashRef: React.RefObject<HTMLDivElement | null>,
): Pick<
  UseWebCrossGridDragReturn,
  "startCrossGridDrag" | "startCrossGridDragAt" | "cancel"
> {
  const { startCrossGridDrag, startCrossGridDragAt, cancel } =
    useWebCrossGridDrag({
      // Drop-to-delete: a release over the trash zone fires onDrop with its id
      // (the adapter already cancelled the pending move, so this isn't racing a
      // stale commit) → remove the item. Other drops commit atomically in the
      // store, so there's nothing else to do here.
      onDrop: (e) => {
        if (e.zoneId === TRASH_ZONE_ID && e.fromGridId) {
          useGridStore.getState().removeItem(e.fromGridId, e.itemId);
        }
      },
      // Screen-space trash zone; `test` reads the live rect of the rendered
      // CanvasTrashZone (null until it mounts on drag-start → false). Disabled in
      // read-only preview (no drags start anyway).
      zones: readOnly
        ? []
        : [
            {
              id: TRASH_ZONE_ID,
              test: (x, y) => {
                const el = trashRef.current;
                if (!el) return false;
                const r = el.getBoundingClientRect();
                return (
                  x >= r.left && x <= r.right && y >= r.top && y <= r.bottom
                );
              },
            },
          ],
      nestAreaRatio: 0.6,
      // Targeting filter — powers the adapter's nearest-accepted-child bump: a
      // widget hovering the page gap between sections targets the nearer
      // section instead of refusing on the page body. Since grid 0.9.4 the
      // adapter passes the REAL dragged item (or external-add spec), so this is
      // the SAME item-aware predicate as the store's membership gate — one
      // rule, pinned defaults included (kind fallback covers item-less frames).
      accepts: (gridId, kind, item) =>
        crossGridAccepts(
          item ?? itemForKind(kind),
          gridId,
          (gid) => useGridStore.getState().grids[gid]?.parentGridId,
        ),
    });
  return useMemo(
    () => ({
      startCrossGridDrag: readOnly ? NOOP_STARTER : startCrossGridDrag,
      startCrossGridDragAt,
      cancel,
    }),
    [readOnly, startCrossGridDrag, startCrossGridDragAt, cancel],
  );
}

function InnerCanvas({
  ydoc,
  readOnly,
  canSeed,
}: {
  ydoc: EditorDoc["ydoc"];
  readOnly: boolean;
  // Only the authoritative tab may create new-site defaults; a follower that
  // hasn't synced yet mounts empty and waits (see EditorDoc.canSeed).
  canSeed: boolean;
}) {
  // Bind + seed the shared header/footer grids ONCE for the whole canvas; every
  // page node then renders those same grids (see PageNode SharedRegionBand).
  useSharedRegions(ydoc, readOnly, canSeed);
  // Cross-grid drag (grid 0.8.42): mount the web adapter ONCE for the whole
  // canvas — it hit-tests every grid in the store via window listeners, so it
  // covers every page's body + section grids. The starter is shared to each
  // item through SitemapInteractionContext (below). The trash ref lets the
  // adapter's drop-to-delete zone read the rendered CanvasTrashZone's rect.
  const trashRef = useRef<HTMLDivElement | null>(null);
  const crossGridDrag = useCrossGridDrag(readOnly, trashRef);
  const startCrossGridDrag = crossGridDrag.startCrossGridDrag;
  // Cross-TAB drag (native DnD): the drop side of an ⌥Alt+drag from another
  // tab (or this one — duplicate) lands through the external-add pipeline.
  useCrossTabDrop(readOnly);
  // Cross-WINDOW carry: ⌥ pressed MID-DRAG switches the live pointer drag
  // into a broadcast copy that other builder windows can receive; ⌥ released
  // switches back to the move. Also mounts this window's receiver. Needs the
  // adapter's `cancel` (end the live session on ⌥ down) and
  // `startCrossGridDragAt` (restart the move drag on ⌥ release).
  useCrossWindowDrag(readOnly, crossGridDrag);
  const initial = useMemo(
    () => readInitialSitemap(ydoc, canSeed),
    [ydoc, canSeed],
  );
  const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initial.edges);
  const store = useStoreApi();
  const {
    screenToFlowPosition,
    flowToScreenPosition,
    getViewport,
    getIntersectingNodes,
    getNodes,
    getEdges,
    getNode,
    fitView,
    setViewport,
  } = useReactFlow();
  const { registerCanvas } = useAddDrag();

  // ── Page navigation (prev/next focus) ──────────────────────────────────────
  const [currentPageId, setCurrentPageId] = useState<string | null>(null);
  // Handle of the in-flight focus tween (rAF id), so a fresh nav can cancel it and
  // retarget mid-flight instead of two tweens fighting over the viewport.
  const focusRaf = useRef<number | null>(null);
  // DFS pre-order of pages: roots (left-to-right) then children (nav order).
  const orderedPageIds = useCallback((): string[] => {
    const nodes = getNodes();
    const edges = getEdges() as unknown as NavEdge[];
    const roots = nodes
      .filter((n) => navParentOf(edges, n.id) === null)
      .sort((a, b) => a.position.x - b.position.x)
      .map((n) => n.id);
    const out: string[] = [];
    const seen = new Set<string>();
    const visit = (id: string) => {
      if (seen.has(id)) return; // cycle guard
      seen.add(id);
      out.push(id);
      for (const c of navChildrenOf(edges, id)) visit(c);
    };
    roots.forEach(visit);
    for (const n of nodes) if (!seen.has(n.id)) out.push(n.id); // orphans (safety)
    return out;
  }, [getNodes, getEdges]);
  // The page whose center is nearest the viewport center — the repair anchor when
  // the tracked current page is gone (delete/reparent) and after a manual pan.
  const nearestPageId = useCallback(
    (ids: readonly string[]): string | null => {
      const vp = getViewport();
      const { width, height } = store.getState();
      const cx = (width / 2 - vp.x) / vp.zoom;
      const cy = (height / 2 - vp.y) / vp.zoom;
      let best: string | null = null;
      let bestD = Infinity;
      for (const id of ids) {
        const n = getNode(id);
        if (!n) continue;
        const w = n.measured?.width ?? PAGE_WIDTH;
        const h = n.measured?.height ?? PAGE_HEIGHT;
        const dx = n.position.x + w / 2 - cx;
        const dy = n.position.y + h / 2 - cy;
        const d = dx * dx + dy * dy;
        if (d < bestD) {
          bestD = d;
          best = id;
        }
      }
      return best;
    },
    [getViewport, store, getNode],
  );
  // Focus a page: horizontally CENTERED but TOP-aligned (page top just below the
  // toolbar) so its header bar is visible, not vertically centered. Zoom fits the
  // page WIDTH to the canvas with ~8% margin, clamped to the editable band.
  const focusPage = useCallback(
    (id: string) => {
      const n = getNode(id);
      if (!n) return;
      const w = n.measured?.width ?? n.width ?? PAGE_WIDTH;
      // `store.width` is the ReactFlow pane width (the whole canvas — the Add /
      // Media panels float over it rather than shrinking it, so this is the right
      // basis for the zoom-to-fit math).
      const paneW =
        store.getState().width ||
        (typeof window !== "undefined" ? window.innerWidth : PAGE_WIDTH);
      const zoom = Math.min(
        1,
        Math.max(EDIT_MIN_ZOOM, (paneW * 0.84) / PAGE_WIDTH),
      );
      const TOP_MARGIN = 72; // gap above the page so the header bar shows
      const to = {
        x: paneW / 2 - (n.position.x + w / 2) * zoom,
        y: TOP_MARGIN - n.position.y * zoom,
        zoom,
      };
      setCurrentPageId(id);

      // Smoothly tween the viewport (pan + zoom together) so a jump reads as travel
      // — you see where you were and where you're going — instead of a jarring cut.
      // Driven by our OWN rAF applying an INSTANT setViewport each frame: ReactFlow's
      // built-in `{duration}` d3 transition is interrupted by the surrounding
      // re-render and silently no-ops here, whereas an instant setViewport always
      // lands. easeInOutCubic gives a calm accelerate/settle.
      if (focusRaf.current !== null) cancelAnimationFrame(focusRaf.current);
      const from = getViewport();
      const t0 = performance.now();
      const DURATION = 420;
      const ease = (t: number) =>
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const tick = () => {
        // Read the clock inside the frame (not the rAF timestamp arg) so t0 and
        // the elapsed reading always share one time origin.
        const t = Math.min(1, (performance.now() - t0) / DURATION);
        const e = ease(t);
        void setViewport({
          x: from.x + (to.x - from.x) * e,
          y: from.y + (to.y - from.y) * e,
          zoom: from.zoom + (to.zoom - from.zoom) * e,
        });
        focusRaf.current = t < 1 ? requestAnimationFrame(tick) : null;
      };
      focusRaf.current = requestAnimationFrame(tick);
    },
    [getNode, getViewport, setViewport, store],
  );
  const gotoPage = useCallback(
    (dir: -1 | 1) => {
      const ids = orderedPageIds();
      if (ids.length === 0) return;
      const cur =
        currentPageId && ids.includes(currentPageId)
          ? currentPageId
          : nearestPageId(ids);
      const idx = cur ? ids.indexOf(cur) : 0;
      const next = idx + dir;
      if (next < 0 || next >= ids.length) return; // clamp at ends
      focusPage(ids[next]);
    },
    [orderedPageIds, currentPageId, nearestPageId, focusPage],
  );

  // Create a page from a dropped palette tile at the drop's flow position.
  // Mirrored to Yjs by the bindYjsToFlow binding (like the default Home
  // node); the new PageNode seeds the template's sections on mount.
  const addPageAt = useCallback(
    (
      screenX: number,
      screenY: number,
      templateId: string,
      targetId?: string,
      mode: "child" | "parent" = "child",
    ) => {
      if (readOnly) return;
      const tpl = PAGE_TEMPLATES.find((t) => t.id === templateId);
      if (!tpl) return;
      const nodesNow = store.getState().nodes as Node<PageNodeData>[];
      // A page nested UNDER a target is auto-placed below it (the drop point only
      // picks the parent); a standalone drop keeps exactly where you dropped it.
      const pos =
        targetId && mode === "child"
          ? positionBelow(
              nodesNow.find((n) => n.id === targetId),
              nodesNow,
            )
          : screenToFlowPosition({ x: screenX, y: screenY });
      const node = createPageNodeFromTemplate(tpl, pos, nodesNow);
      setNodes((nds) => [...nds, node as unknown as Node<PageNodeData>]);
      if (!targetId) return;
      setEdges((es) => {
        if (mode === "parent") {
          // Insert the new page ABOVE the target: it takes the target's old
          // parent slot, and the target becomes its child.
          const grandparent = parentOf(es, targetId);
          let next = es;
          if (grandparent) next = reparent(next, grandparent, node.id).edges;
          return reparent(next, node.id, targetId).edges;
        }
        // Nest the new page UNDER the target.
        return reparent(es, targetId, node.id).edges;
      });
    },
    [readOnly, screenToFlowPosition, store, setNodes, setEdges],
  );

  // Native hit-test: the page node under a screen point + its on-screen rect
  // (for the drop highlight). The native AddDragProvider can't use
  // document.elementFromPoint, so it asks the canvas (which owns
  // screenToFlowPosition / getIntersectingNodes / flowToScreenPosition).
  // Unused on web.
  const pageAt = useCallback(
    (screenX: number, screenY: number) => {
      const p = screenToFlowPosition({ x: screenX, y: screenY });
      const hits = getIntersectingNodes(
        { x: p.x, y: p.y, width: 1, height: 1 },
        true,
      );
      if (hits.length === 0) return null;
      // Topmost (last-painted) wins.
      const node = hits[hits.length - 1]!;
      const tl = flowToScreenPosition({
        x: node.position.x,
        y: node.position.y,
      });
      const zoom = getViewport().zoom;
      const w = (node.measured?.width ?? node.width ?? PAGE_WIDTH) * zoom;
      const h = (node.measured?.height ?? node.height ?? PAGE_HEIGHT) * zoom;
      return {
        id: node.id,
        rect: { x: tl.x, y: tl.y, width: w, height: h },
        zoom,
      };
    },
    [
      screenToFlowPosition,
      getIntersectingNodes,
      flowToScreenPosition,
      getViewport,
    ],
  );

  // Register the page-creator + hit-test with AddDragProvider (which lives
  // outside the ReactFlowProvider and can't call useReactFlow itself). Cleared
  // on unmount so a teardown after this canvas is gone never calls a stale fn.
  useEffect(() => {
    if (readOnly) return;
    registerCanvas({ addPageAt, pageAt });
    return () => registerCanvas(null);
  }, [registerCanvas, addPageAt, pageAt, readOnly]);

  // Persist the sitemap into the site's Y.Doc. Without this binding the
  // nodes/edges live only in xyflow's React store and never reach
  // getMap("nodes")/getMap("edges") — so publishSite serializes 0 pages
  // and the published site renders empty (404). The binding seeds the
  // empty doc from the store on mount (the DEFAULT_NODES Home page) and
  // mirrors every subsequent edit store→Yjs.
  useEffect(() => {
    const binding = bindYjsToFlow(
      ydoc,
      store as unknown as FlowStore<Node<PageNodeData>, Edge>,
      {
        // We drive xyflow in CONTROLLED mode (useNodesState), so undo/remote
        // changes must go through the React setters — not xyflow's internal
        // store, which the `nodes` prop overrides on the next render.
        applyRemote: (nodes, edges) => {
          setNodes(nodes);
          setEdges(edges);
        },
      },
    );
    return () => binding.destroy();
  }, [ydoc, store, setNodes, setEdges]);

  const onConnect: OnConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges],
  );

  // ── Page action menu (node "page menu") ──────────────────────────
  const [menu, setMenu] = useState<NodeMenuState | null>(null);
  // "Change URL…" slug-override editor, anchored where the menu was.
  const [slugEdit, setSlugEdit] = useState<{
    id: string;
    x: number;
    y: number;
    label: string;
    slug?: string;
  } | null>(null);
  const [moveUnderOpen, setMoveUnderOpen] = useState(false);
  const closeMenu = useCallback(() => {
    setMenu(null);
    setMoveUnderOpen(false);
  }, []);

  // ── Drag-to-parent + tidy layout (R2) ────────────────────────────
  const [dropParentId, setDropParentId] = useState<string | null>(null);
  // Which relationship the committed drop makes: "child" = dragged nests under
  // the target (bottom half); "parent" = dragged becomes the target's parent
  // (top half). Drives the PageNode affordance + the reparent direction.
  const [dropMode, setDropMode] = useState<"parent" | "child">("child");
  const [autoArrange, setAutoArrange] = useState(false);
  // Which device the canvas authors for (view state — reframes every page).
  const [breakpoint, setBreakpoint] = useState<Breakpoint>("desktop");
  // Push the active breakpoint into the y-grid bindings: switching re-seeds
  // every bound grid's store with that breakpoint's resolved layout (mobile
  // overrides ↔ desktop base). One call for the whole canvas (module singleton).
  useEffect(() => {
    setActiveBreakpoint(breakpoint);
  }, [breakpoint]);
  // Fluid type (fluidType.ts) scales against `--wb-vw`. The clamp lives in a
  // custom property (`--fontSizeN`) declared on each page body (ThemeVars), so
  // its nested `var(--wb-vw)` resolves there, against the value the body
  // inherits. The canvas sets `--wb-vw` once on the canvas scope from the
  // active authoring width; switching device then reframes the type, not just
  // the page width. (Read-only/preview leaves it unset → 100vw, matching publish.)
  const authoringWidth = useAuthoringWidth(breakpoint);
  useEffect(() => {
    if (readOnly) return;
    const scope = document.querySelector<HTMLElement>("[data-wb-scope]");
    if (!scope) return;
    scope.style.setProperty("--wb-vw", `${authoringWidth}px`);
    return () => {
      scope.style.removeProperty("--wb-vw");
    };
  }, [authoringWidth, readOnly]);
  const [undo, setUndo] = useState<{ prevEdges: Edge[]; label: string } | null>(
    null,
  );
  // Dwell tracking: the candidate the dragged center currently hovers + a timer
  // that promotes it to `dropParentId` only after it lingers (deliberate drop).
  const dwell = useRef<{ cand: string | null; timer: number | null }>({
    cand: null,
    timer: null,
  });
  const clearDwell = useCallback(() => {
    if (dwell.current.timer != null) window.clearTimeout(dwell.current.timer);
    dwell.current = { cand: null, timer: null };
  }, []);

  const openNodeMenu = useCallback(
    (nodeId: string, x: number, y: number) => {
      if (readOnly) return;
      setMoveUnderOpen(false);
      setMenu({ id: nodeId, x, y });
    },
    [readOnly],
  );

  // The URL pencil (selected page's titlebar) opens the same slug editor the ⋯
  // "Change URL…" item does, anchored where the pencil was clicked.
  const openSlugEditor = useCallback(
    (nodeId: string, x: number, y: number, label: string, slug?: string) => {
      if (readOnly) return;
      setSlugEdit({ id: nodeId, x, y, label, ...(slug ? { slug } : {}) });
    },
    [readOnly],
  );

  const interaction = useMemo(
    () => ({
      openNodeMenu,
      openSlugEditor,
      dropParentId,
      dropMode,
      breakpoint,
      startCrossGridDrag,
    }),
    [
      openNodeMenu,
      openSlugEditor,
      dropParentId,
      dropMode,
      breakpoint,
      startCrossGridDrag,
    ],
  );

  const addChildPage = useCallback(
    (parentId: string) => {
      const tpl =
        PAGE_TEMPLATES.find((t) => t.id === "blank") ?? PAGE_TEMPLATES[0]!;
      const nodesNow = store.getState().nodes as Node<PageNodeData>[];
      const parent = nodesNow.find((n) => n.id === parentId);
      // Auto-arrange just this page: below the parent's real (measured) bottom,
      // clear of any other node — not a fixed offset, and not a whole-graph tidy.
      const pos = positionBelow(parent, nodesNow);
      const node = createPageNodeFromTemplate(tpl, pos, nodesNow);
      setNodes((nds) => [...nds, node as unknown as Node<PageNodeData>]);
      setEdges((es) => reparent(es, parentId, node.id).edges);
      closeMenu();
    },
    [store, setNodes, setEdges, closeMenu],
  );

  const moveUnder = useCallback(
    (parentId: string, childId: string) => {
      setEdges((es) => reparent(es, parentId, childId).edges);
      closeMenu();
    },
    [setEdges, closeMenu],
  );

  const detachNode = useCallback(
    (id: string) => {
      setEdges((es) => detach(es, id).edges);
      closeMenu();
    },
    [setEdges, closeMenu],
  );

  // Set or clear a page's URL-slug override (empty → back to name-derived).
  const setSlug = useCallback(
    (id: string, slug: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, slug: slug || undefined } }
            : n,
        ),
      );
    },
    [setNodes],
  );

  const deleteNode = useCallback(
    (id: string) => {
      purgePageGrids(ydoc, id);
      // Write the node/edge removal into the DOC directly, not only via the
      // store→Yjs mirror: deleting the LAST page transitions the store to
      // completely empty, which the binding deliberately ignores (that same
      // signature is xyflow's unmount reset — mirroring it broadcast a
      // wipe-everything to every other window on this site). Idempotent with
      // the mirror when other pages remain.
      ydoc.transact(() => {
        ydoc.getMap("nodes").delete(id);
        const yEdges = ydoc.getMap("edges");
        yEdges.forEach((v, k) => {
          const e = v as { source?: string; target?: string };
          if (e?.source === id || e?.target === id) yEdges.delete(k);
        });
      }, FLOW_LOCAL_ORIGIN);
      setNodes((nds) => nds.filter((n) => n.id !== id));
      setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
      closeMenu();
    },
    [ydoc, setNodes, setEdges, closeMenu],
  );

  const menuParentExists =
    menu != null && edges.some((e) => e.target === menu.id);

  // Candidate under the dragged node's CENTER (excludes self/descendants), plus
  // whether the center is over the candidate's TOP half (the dragged page becomes
  // the candidate's PARENT) or BOTTOM half (the dragged nests as a CHILD).
  const detectDropParent = useCallback(
    (dragged: Node): { id: string; mode: "parent" | "child" } | null => {
      const box = nodeBox(dragged);
      const center = { x: box.x + box.w / 2, y: box.y + box.h / 2 };
      const candidates = getIntersectingNodes(dragged).map(nodeBox);
      const id = pickDropParent(dragged.id, center, candidates, getEdges());
      if (!id) return null;
      const cand = candidates.find((c) => c.id === id);
      const mode: "parent" | "child" =
        cand && center.y < cand.y + cand.h / 2 ? "parent" : "child";
      return { id, mode };
    },
    [getIntersectingNodes, getEdges],
  );

  const onNodeDrag: OnNodeDrag = useCallback(
    (_e, node) => {
      if (readOnly) return;
      const hit = detectDropParent(node);
      const cand = hit?.id ?? null;
      if (cand === dwell.current.cand) {
        // Same candidate — live-switch parent/child as the pointer crosses the
        // vertical mid (only meaningful once the dwell has committed the hint).
        if (hit && dropParentId === cand) setDropMode(hit.mode);
        return;
      }
      // Candidate changed → reset the dwell timer and the (uncommitted) hint.
      if (dwell.current.timer != null) window.clearTimeout(dwell.current.timer);
      dwell.current.cand = cand;
      setDropParentId(null);
      if (hit) setDropMode(hit.mode);
      dwell.current.timer = cand
        ? window.setTimeout(() => setDropParentId(cand), DROP_DWELL_MS)
        : null;
    },
    [readOnly, detectDropParent, dropParentId],
  );

  const onNodeDragStop: OnNodeDrag = useCallback(
    (_e, node) => {
      const committed = dropParentId; // only set once dwell elapsed
      const mode = dropMode;
      clearDwell();
      setDropParentId(null);
      if (!committed || committed === node.id) return;
      const prevEdges = getEdges() as Edge[];
      // child (bottom): dragged nests under the target. parent (top): dragged
      // becomes the target's parent — reparent the TARGET under the dragged.
      const { edges: next, changed } =
        mode === "parent"
          ? reparent(prevEdges, node.id, committed)
          : reparent(prevEdges, committed, node.id);
      if (!changed) return;
      setEdges(next);
      const otherLabel = String(
        (getNodes().find((n) => n.id === committed)?.data as PageNodeData)
          ?.label ?? committed,
      );
      setUndo({ prevEdges, label: otherLabel });
    },
    [dropParentId, dropMode, clearDwell, getEdges, getNodes, setEdges],
  );

  // Arrange nodes as a top-down tree. `fit` re-centres the viewport — the manual
  // "Tidy layout" button does so; auto-arrange does NOT on every change, so a
  // re-layout triggered by editing a page (e.g. it grew taller) keeps pages tidy
  // without yanking the canvas out from under the user.
  const applyLayout = useCallback(
    (fit: boolean) => {
      setNodes(
        (nds) =>
          layoutSitemap(
            nds as Node<PageNodeData>[],
            getEdges(),
          ) as Node<PageNodeData>[],
      );
      if (fit)
        requestAnimationFrame(() => fitView({ duration: 300, padding: 0.2 }));
    },
    [setNodes, getEdges, fitView],
  );
  const tidyLayout = useCallback(() => applyLayout(true), [applyLayout]);

  // Auto-arrange: re-tidy whenever a layout INPUT changes — the node set, the
  // edges, OR a node's measured SIZE. A page that grew (e.g. columns added) makes
  // `layoutSitemap` want more room, so the rows below must be pushed down or it
  // overlaps them. Position-only moves are excluded (see layoutInputKey) so the
  // layout-driven setNodes (which only changes positions) can't loop.
  const layoutKey = useMemo(() => layoutInputKey(nodes, edges), [nodes, edges]);
  const lastTidyKey = useRef<string | null>(null);
  useEffect(() => {
    if (!autoArrange) {
      lastTidyKey.current = null;
      return;
    }
    if (lastTidyKey.current === layoutKey) return;
    // Fit the viewport only on the first tidy after turning auto-arrange on; later
    // re-layouts (a page resized, a page added) reposition silently.
    const firstRun = lastTidyKey.current === null;
    lastTidyKey.current = layoutKey;
    applyLayout(firstRun);
  }, [autoArrange, layoutKey, applyLayout]);

  // Auto-dismiss the undo toast.
  useEffect(() => {
    if (!undo) return;
    const t = window.setTimeout(() => setUndo(null), 6000);
    return () => window.clearTimeout(t);
  }, [undo]);

  const undoReparent = useCallback(() => {
    if (undo) setEdges(undo.prevEdges);
    setUndo(null);
  }, [undo, setEdges]);

  // Pages drag ONLY from their header (the `.wb-page-drag` bar in PageNode), not
  // from anywhere on the body — so editing content never moves the page, and no
  // widget needs an xyflow `nodrag` class (which react-strict-dom can't set).
  // Injected at the render boundary so `dragHandle` never round-trips to Yjs.
  // So is each page's accessible name: xyflow renders a node as an unnamed
  // role="group" unless it carries `ariaLabel`.
  const draggableNodes = useMemo(
    () =>
      nodes.map((n) => ({
        ...n,
        ariaLabel: `Page “${n.data.label}”`,
        ...(readOnly ? {} : { dragHandle: ".wb-page-drag" }),
      })),
    [nodes, readOnly],
  );

  return (
    <SitemapInteractionProvider value={interaction}>
      <FlowCanvasFrame>
        <ReactFlow
          nodes={draggableNodes}
          edges={edges}
          nodeTypes={NODE_TYPES}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={readOnly ? undefined : onConnect}
          onNodeDrag={readOnly ? undefined : onNodeDrag}
          onNodeDragStop={readOnly ? undefined : onNodeDragStop}
          onNodeContextMenu={
            readOnly
              ? undefined
              : (e, node) => {
                  e.preventDefault();
                  openNodeMenu(node.id, e.clientX, e.clientY);
                }
          }
          nodesDraggable={!readOnly}
          nodesConnectable={!readOnly}
          elementsSelectable={!readOnly}
          minZoom={0.1}
          maxZoom={2}
          fitView
          // Hide the "React Flow" attribution badge (bottom-right). `proOptions`
          // is a @xyflow/react-only prop; the RN xyflow port's props type omits
          // it, and SitemapCanvas is shared, so pass it via a SPREAD (skips JSX
          // excess-property checks) rather than a direct attribute — valid on web
          // and a harmless no-op on the native backend.
          {...({
            proOptions: { hideAttribution: true },
          } as { proOptions?: { hideAttribution: boolean } })}
        >
          <Background />
          {/* xyflow's built-in <Controls/> (bottom-left zoom/fit/lock) is a
              duplicate of our own zoom controls in CanvasToolsPanel (bottom
              center) — omitted so there's one control surface. */}
          <MiniMap
            nodeColor={() => "#e5e7eb"}
            maskColor="rgba(249,250,251,0.7)"
          />
          {!readOnly && (
            <Panel position="top-right">
              {/* Toolbar + the contextual override strip stacked beneath it, so
                  the strip stays tied to the switcher and never collides with the
                  floating Add/Format panels (top-left / top-center). */}
              <html.div style={canvasChrome.toolbarStack}>
                <ChromeBox variant="toolbar">
                  <BreakpointSwitcher
                    value={breakpoint}
                    onChange={setBreakpoint}
                  />
                  <EditorButton
                    onClick={() => gotoPage(-1)}
                    title="Focus the previous page"
                    aria-label="Previous page"
                  >
                    ‹
                  </EditorButton>
                  <EditorButton
                    onClick={() => gotoPage(1)}
                    title="Focus the next page"
                    aria-label="Next page"
                  >
                    ›
                  </EditorButton>
                  <EditorButton
                    onClick={tidyLayout}
                    title="Arrange pages as a tidy tree"
                  >
                    Tidy layout
                  </EditorButton>
                  <CanvasToggle checked={autoArrange} onChange={setAutoArrange}>
                    Auto-arrange
                  </CanvasToggle>
                </ChromeBox>
                <BreakpointHint breakpoint={breakpoint} />
              </html.div>
            </Panel>
          )}
          {undo && (
            <Panel position="bottom-center">
              <ChromeBox variant="toast">
                <html.span style={canvasChrome.toastText}>
                  Nested under “{undo.label}”
                </html.span>
                <EditorButton onClick={undoReparent} variant="link">
                  Undo
                </EditorButton>
              </ChromeBox>
            </Panel>
          )}
        </ReactFlow>
        <CanvasZoomPublisher />
        {!readOnly && <CanvasTrashZone rootRef={trashRef} />}
        {!readOnly && <CanvasToolsPanel />}
        {menu &&
          (moveUnderOpen ? (
            <EditorMenu
              x={menu.x}
              y={menu.y}
              onClose={closeMenu}
              ariaLabel="Move under page"
            >
              {candidateParents(
                nodes as { id: string; data: PageNodeData }[],
                edges,
                menu.id,
              ).map((n) => (
                <EditorMenuItem
                  key={n.id}
                  onSelect={() => moveUnder(n.id, menu.id)}
                >
                  {String((n.data as PageNodeData)?.label ?? n.id)}
                </EditorMenuItem>
              ))}
            </EditorMenu>
          ) : (
            <EditorMenu
              x={menu.x}
              y={menu.y}
              onClose={closeMenu}
              ariaLabel="Page actions"
            >
              <EditorMenuItem onSelect={() => addChildPage(menu.id)}>
                Add child page
              </EditorMenuItem>
              <EditorMenuItem onSelect={() => setMoveUnderOpen(true)}>
                Move under…
              </EditorMenuItem>
              <EditorMenuItem
                onSelect={() => detachNode(menu.id)}
                disabled={!menuParentExists}
              >
                Detach from parent
              </EditorMenuItem>
              {/* Rename lives on the node itself now — the pencil next to the
                page name (uniform on desktop + touch), not a menu item. */}
              {/* The home page's URL is always `/` — no slug to change. */}
              {menu.id !== "home" && (
                <EditorMenuItem
                  onSelect={() => {
                    const node = (nodes as Node<PageNodeData>[]).find(
                      (n) => n.id === menu.id,
                    );
                    const d = node?.data;
                    setSlugEdit({
                      id: menu.id,
                      x: menu.x,
                      y: menu.y,
                      label: d?.label ?? menu.id,
                      slug: d?.slug,
                    });
                    closeMenu();
                  }}
                >
                  Change URL…
                </EditorMenuItem>
              )}
              <EditorMenuItem onSelect={() => deleteNode(menu.id)} danger>
                Delete page
              </EditorMenuItem>
            </EditorMenu>
          ))}
        {slugEdit && (
          <SlugEditor
            x={slugEdit.x}
            y={slugEdit.y}
            label={slugEdit.label}
            slugOverride={slugEdit.slug}
            onSave={(s) => setSlug(slugEdit.id, s)}
            onReset={() => setSlug(slugEdit.id, "")}
            onClose={() => setSlugEdit(null)}
          />
        )}
      </FlowCanvasFrame>
    </SitemapInteractionProvider>
  );
}

// ── Public component ──────────────────────────────────────────────

interface SitemapCanvasProps {
  editorDoc: EditorDoc;
  /** Render the canvas non-editable (version preview): no drag/resize,
   *  no connection handles, all widgets in preview mode. */
  readOnly?: boolean;
}

export function SitemapCanvas({ editorDoc, readOnly }: SitemapCanvasProps) {
  const ro = readOnly ?? false;
  return (
    <ReadOnlyProvider value={ro}>
      <EditorProvider value={editorDoc}>
        <ReactFlowProvider>
          <InnerCanvas
            ydoc={editorDoc.ydoc}
            readOnly={ro}
            // A read-only preview is pre-seeded from its snapshot, so seeding
            // must stay off there; otherwise defer to the doc's authority flag.
            canSeed={!ro && editorDoc.canSeed}
          />
        </ReactFlowProvider>
      </EditorProvider>
    </ReadOnlyProvider>
  );
}
